class CustomWishlist {
  constructor() {
    this.storageKey = 'custom_wishlist';
    this.wishlist = this.getWishlist();
    this.notificationTimeout = null;
    this.init();
  }

  init() {
    // Update all buttons on page load
    this.updateAllButtons();
    this.updateHeaderCount();
    
    // Bind click events
    document.addEventListener('click', this.handleClick.bind(this));
    
    // Listen for storage changes
    window.addEventListener('storage', this.handleStorageChange.bind(this));
    
    // Initialize on page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.updateAllButtons();
        this.updateHeaderCount();
      });
    } else {
      this.updateAllButtons();
      this.updateHeaderCount();
    }
  }

  handleClick(event) {
    const wishlistBtn = event.target.closest('[data-wishlist-btn]');
    if (!wishlistBtn) return;
    
    event.preventDefault();
    
    const productId = wishlistBtn.dataset.productId;
    const productHandle = wishlistBtn.dataset.productHandle;
    const productTitle = wishlistBtn.dataset.productTitle;
    const productImage = wishlistBtn.dataset.productImage;
    const productPrice = wishlistBtn.dataset.productPrice;
    
    if (!productId) {
      console.error('No product ID found');
      return;
    }
    
    this.toggleItem(productId, productHandle, productTitle, productImage, productPrice);
    this.updateButton(wishlistBtn, productId);
    this.updateHeaderCount();
    
    // Visual feedback
    wishlistBtn.classList.add('wishlist-btn--added');
    setTimeout(() => {
      wishlistBtn.classList.remove('wishlist-btn--added');
    }, 400);
    
    // Show notification with image
    this.showNotification(productId, productTitle, productImage);
  }

  getWishlist() {
    try {
      const wishlistData = localStorage.getItem(this.storageKey);
      return wishlistData ? JSON.parse(wishlistData) : [];
    } catch (error) {
      console.error('Error reading wishlist:', error);
      return [];
    }
  }

  saveWishlist() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.wishlist));
      this.dispatchUpdateEvent();
    } catch (error) {
      console.error('Error saving wishlist:', error);
    }
  }

  addItem(productId, productHandle, productTitle, productImage, productPrice) {
    if (this.isInWishlist(productId)) return false;
    
    // Get the featured image URL without size modifier
    const featuredImage = productImage ? 
      productImage.replace(/_150x\.jpg/, '.jpg')
                  .replace(/_150x\.png/, '.png')
                  .replace(/_150x\./, '.') : 
      '';
    
    this.wishlist.push({
      id: productId,
      handle: productHandle,
      title: productTitle,
      image: featuredImage,
      price: productPrice,
      addedAt: new Date().toISOString()
    });
    
    this.saveWishlist();
    return true;
  }

  removeItem(productId) {
    const index = this.wishlist.findIndex(item => item.id === productId);
    if (index === -1) return false;
    
    this.wishlist.splice(index, 1);
    this.saveWishlist();
    return true;
  }

  toggleItem(productId, productHandle, productTitle, productImage, productPrice) {
    if (this.isInWishlist(productId)) {
      return this.removeItem(productId);
    } else {
      return this.addItem(productId, productHandle, productTitle, productImage, productPrice);
    }
  }

  isInWishlist(productId) {
    return this.wishlist.some(item => item.id === productId);
  }

  getItem(productId) {
    return this.wishlist.find(item => item.id === productId);
  }

  getCount() {
    return this.wishlist.length;
  }

  clearWishlist() {
    this.wishlist = [];
    this.saveWishlist();
  }

  updateButton(button, productId) {
    const isInWishlist = this.isInWishlist(productId);
    const iconContainer = button.querySelector('.wishlist-btn__icon');
    
    if (iconContainer) {
      if (isInWishlist) {
        button.classList.add('wishlist-btn--active');
        button.setAttribute('aria-label', 'Remove from wishlist');
        button.setAttribute('title', 'Remove from wishlist');
        iconContainer.innerHTML = `
          <svg class="icon-heart icon-heart--filled" width="22" height="22" viewBox="0 0 256 256" fill="currentColor">
             <path d="M240,98a57.63,57.63,0,0,1-17,41L133.7,229.62a8,8,0,0,1-11.4,0L33,139a58,58,0,0,1,82-82.1L128,69.05l13.09-12.19A58,58,0,0,1,240,98Z"></path>
          </svg>
        `;
      } else {
        button.classList.remove('wishlist-btn--active');
        button.setAttribute('aria-label', 'Add to wishlist');
        button.setAttribute('title', 'Add to wishlist');
        iconContainer.innerHTML = `
          <svg class="icon-heart icon-heart--outline" width="22" height="22" viewBox="0 0 256 256" fill="currentColor" >
             <path d="M223,57a58.07,58.07,0,0,0-81.92-.1L128,69.05,114.91,56.86A58,58,0,0,0,33,139l89.35,90.66a8,8,0,0,0,11.4,0L223,139a58,58,0,0,0,0-82Zm-11.35,70.76L128,212.6,44.3,127.68a42,42,0,0,1,59.4-59.4l.2.2,18.65,17.35a8,8,0,0,0,10.9,0L152.1,68.48l.2-.2a42,42,0,1,1,59.36,59.44Z"></path>
          </svg>
        `;
      }
    }
  }

  updateAllButtons() {
    const buttons = document.querySelectorAll('[data-wishlist-btn]');
    buttons.forEach(button => {
      const productId = button.dataset.productId;
      if (productId) {
        this.updateButton(button, productId);
      }
    });
    this.updateHeaderCount();
  }

  updateHeaderCount() {
    const headerCountElements = document.querySelectorAll(
      '.wishlist-header-count, ' +
      '.header__icon--wishlist .wishlist-count, ' +
      '[data-header-wishlist-count]'
    );
    
    const totalCount = this.getCount();
    
    headerCountElements.forEach(element => {
      if (totalCount > 0) {
        element.textContent = totalCount;
        element.style.display = 'flex';
      } else {
        element.textContent = '';
        element.style.display = 'none';
      }
    });
  }

  showNotification(productId, productTitle, productImage = null) {
    const isInWishlist = this.isInWishlist(productId);
    const message = isInWishlist 
      ? `✓ Added "${productTitle}" to wishlist`
      : `Removed "${productTitle}" from wishlist`;
    
    // Remove any existing notification
    const existingNotification = document.querySelector('.wishlist-notification');
    if (existingNotification) {
      existingNotification.remove();
    }
    
    // Clear any existing timeout
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout);
    }
    
    // Create notification element
    const notification = document.createElement('div');
    notification.className = 'wishlist-notification';
    notification.setAttribute('role', 'alert');
    notification.setAttribute('aria-live', 'polite');
    
    // Create notification HTML
    notification.innerHTML = `
      <div class="notification-content">
        <div class="notification-image-container">
          ${productImage ? 
            `<img src="${productImage}" alt="${productTitle}" class="notification-image" 
                 onerror="this.src='data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHZpZXdCb3g9IjAgMCA1MCA1MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIGZpbGw9IiNGNUY1RjUiLz48cGF0aCBkPSJNMzAgMjBDMzAgMjIuMjA5MSAyOC4yMDkxIDI0IDI2IDI0QzIzLjc5MDkgMjQgMjIgMjIuMjA5MSAyMiAyMEMyMiAxNy43OTA5IDIzLjc5MDkgMTYgMjYgMTZDMjguMjA5MSAxNiAzMCAxNy43OTA5IDMwIDIwWiIgZmlsbD0iI0NEQ0RDRCIvPjxwYXRoIGQ9Ik0zMy41IDM0SDE4LjVDMTcuMTE5MyAzNCAxNiAzMi44ODA3IDE2IDMxLjVWMjEuNUMxNiAyMC4xMTkzIDE3LjExOTMgMTkgMTguNSAxOUgzMS41QzMyLjg4MDcgMTkgMzQgMjAuMTE5MyAzNCAyMS41VjMxLjVDMzQgMzIuODgwNyAzMi44ODA3IDM0IDMxLjUgMzRaTTE4LjUgMjEuNVYzMS41SDMxLjVWMjEuNUgxOC41WiIgZmlsbD0iI0NEQ0RDRCIvPjwvc3ZnPg==';">` : 
            `<div class="notification-placeholder">
               <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                 <path d="M12 21.35L10.55 20.03C5.4 15.36 2 12.28 2 8.5C2 5.42 4.42 3 7.5 3C9.24 3 10.91 3.81 12 5.09C13.09 3.81 14.76 3 16.5 3C19.58 3 22 5.42 22 8.5C22 12.28 18.6 15.36 13.45 20.04L12 21.35Z" fill="${isInWishlist ? '#4CAF50' : '#f44336'}"/>
               </svg>
             </div>`
          }
        </div>
        <div class="notification-text">
          <div class="notification-title">${message}</div>
          <div class="notification-status">${isInWishlist ? 'Successfully added' : 'Removed from wishlist'}</div>
        </div>
        <button class="notification-close" aria-label="Close notification">
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M13 1L1 13M1 1L13 13" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
        </button>
      </div>
      <div class="notification-progress" style="background-color: ${isInWishlist ? '#4CAF50' : '#f44336'}"></div>
    `;
    
    document.body.appendChild(notification);
    
    // Add event listeners
    const closeBtn = notification.querySelector('.notification-close');
    closeBtn.addEventListener('click', () => {
      this.hideNotification(notification);
    });
    
    // Show notification with slide-in effect
    requestAnimationFrame(() => {
      notification.classList.add('show');
      // Start progress bar animation
      const progressBar = notification.querySelector('.notification-progress');
      if (progressBar) {
        progressBar.style.animation = 'progress 4s linear forwards';
      }
    });
    
    // Auto hide after 4 seconds
    this.notificationTimeout = setTimeout(() => {
      this.hideNotification(notification);
    }, 4000);
    
    // Keep notification visible on hover
    notification.addEventListener('mouseenter', () => {
      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout);
      }
      const progressBar = notification.querySelector('.notification-progress');
      if (progressBar) {
        progressBar.style.animationPlayState = 'paused';
      }
    });
    
    notification.addEventListener('mouseleave', () => {
      this.notificationTimeout = setTimeout(() => {
        this.hideNotification(notification);
      }, 1000);
      
      const progressBar = notification.querySelector('.notification-progress');
      if (progressBar) {
        progressBar.style.animationPlayState = 'running';
      }
    });
  }

  hideNotification(notification) {
    notification.classList.remove('show');
    notification.classList.add('hide');
    
    setTimeout(() => {
      if (notification.parentElement) {
        notification.remove();
      }
    }, 300);
  }

  handleStorageChange(event) {
    if (event.key === this.storageKey) {
      this.wishlist = this.getWishlist();
      this.updateAllButtons();
      this.updateHeaderCount();
    }
  }

  dispatchUpdateEvent() {
    document.dispatchEvent(new CustomEvent('wishlist:updated', {
      detail: { wishlist: this.wishlist }
    }));
  }
}

// CSS styles to add to your stylesheet
const wishlistStyles = `
.wishlist-notification {
  position: fixed;
  top: 20px;
  right: 20px;
  background: white;
  color: #333;
  padding: 0;
  border-radius: 8px;
  z-index: 99999;
  transform: translateX(120%);
  transition: transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  font-size: 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  max-width: 380px;
  min-width: 320px;
  pointer-events: auto;
}

.wishlist-notification.show {
  transform: translateX(0);
}

.wishlist-notification.hide {
  transform: translateX(120%);
}

.notification-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
}

.notification-image-container {
  width: 50px;
  height: 50px;
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.notification-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
}

.notification-text {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-weight: 600;
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
}

.notification-status {
  font-size: 12px;
  color: #666;
}

.notification-close {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 4px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-left: 4px;
}

.notification-close:hover {
  background: #f0f0f0;
  color: #333;
}

.notification-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: 100%;
  transform-origin: left;
}

@keyframes progress {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}

/* Button animations */
.wishlist-btn--added {
  animation: wishlistPulse 0.4s ease;
}

@keyframes wishlistPulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.15); }
  100% { transform: scale(1); }
}

/* Responsive styles */
@media (max-width: 768px) {
  .wishlist-notification {
    top: 10px;
    right: 10px;
    left: 10px;
    max-width: none;
    min-width: auto;
  }
}
`;

// Add styles to document
const styleSheet = document.createElement('style');
styleSheet.textContent = wishlistStyles;
document.head.appendChild(styleSheet);

// Initialize wishlist
const customWishlist = new CustomWishlist();

// Make available globally
window.customWishlist = customWishlist;

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CustomWishlist;
}